import algoliasearch from 'algoliasearch';
import pkg from 'lodash';
import { useConfigStore } from '~/store/config';
import type { SellerClub, SellerPerson } from '~/constants/types/seller';
const { uniqBy } = pkg;

type algoliaQuery = {
  indexName: string,
  query: string,
  params?: {
    hitsPerPage: number,
  },
}

type AlgoliaClubResult = {
  hits: SellerClub[]
}

type AlgoliaSellerResult = {
  hits: SellerPerson[]
}

type AlgoliaSellerSearchResult = {
  hits: SellerPerson[];
  usedIndex: string;
}

export default function useAlgoliaSearch() {
  const config = useConfigStore();
  const runtimeConfig = useRuntimeConfig();

  const client = algoliasearch(
    runtimeConfig.public.algoliaId,
    runtimeConfig.public.algoliaSecret
  );

  const algoliaClubSearch = async(input: string, indexPrefix?: string) => {
    // Use provided indexPrefix or default to current market's index
    const algoliaBaseIndex = indexPrefix || config.algoliaBaseIndex.replace('fi-sv', 'fi');
    const index = client.initIndex(`${algoliaBaseIndex}_groups`);
    const res = await index.search<AlgoliaClubResult>(
      `"${input}"`, // quotes for exact match only
      {
        typoTolerance: false,
      }
    );
    if (res) {
      return res.hits as unknown as SellerClub[];
    } else {
      console.error();
    }
    return [];
  };

  const algoliaSellerSearch = async(input: string): Promise<AlgoliaSellerSearchResult> => {
    const runtimeConfig = useRuntimeConfig();
    const algoliaIndexPrefix = runtimeConfig.public.algoliaIndexPrefix;
    const algoliaBaseIndex = config.algoliaBaseIndex.replace('fi-sv', 'fi');
    
    // Determine fallback index (SE ↔ FI)
    let fallbackIndex: string | null = null;
    if (algoliaBaseIndex.includes('_se')) {
      fallbackIndex = `${algoliaIndexPrefix}fi`;
    } else if (algoliaBaseIndex.includes('_fi')) {
      fallbackIndex = `${algoliaIndexPrefix}se`;
    }
    
    // Search in primary index
    const primaryIndex = client.initIndex(`${algoliaBaseIndex}_sellers`);
    const primaryRes = await primaryIndex.search<AlgoliaSellerResult>(
      `"${input}"`,
      {
        typoTolerance: false,
      }
    );
    
    if (primaryRes && primaryRes.hits.length > 0) {
      return {
        hits: primaryRes.hits as unknown as SellerPerson[],
        usedIndex: algoliaBaseIndex,
      };
    }
    
    // If no hits in primary and fallback exists, try fallback index
    if (fallbackIndex) {
      console.log(`[Algolia] No seller found in ${algoliaBaseIndex}_sellers, trying fallback: ${fallbackIndex}_sellers`);
      const fallbackIndexClient = client.initIndex(`${fallbackIndex}_sellers`);
      const fallbackRes = await fallbackIndexClient.search<AlgoliaSellerResult>(
        `"${input}"`,
        {
          typoTolerance: false,
        }
      );
      
      if (fallbackRes && fallbackRes.hits.length > 0) {
        console.log(`[Algolia] Seller found in fallback index: ${fallbackIndex}_sellers`);
        return {
          hits: fallbackRes.hits as unknown as SellerPerson[],
          usedIndex: fallbackIndex,
        };
      }
    }
    
    // No hits in either index
    return {
      hits: [],
      usedIndex: algoliaBaseIndex,
    };
  };

  const algoliaProductSearch = async(input: string) => {
    const index = client.initIndex(`${config.algoliaBaseIndex}_products`);
    client.clearCache();
    let output = [];

    await index
      .search(input, {
        hitsPerPage: 100,
        clickAnalytics: true, // Enable queryID in results for tracking
      })
      .then((res) => {
        const { hits } = res;
        output = uniqBy(hits, 'centraVariant').flatMap((r: any) => {
          return (
            r.items?.map((i: any) => {
              return {
                ...r,
                name: `${r.title} - ${i.name}`,
              };
            }) ?? [r]
          );
        });
      });
    return output;
  };

  const algoliaMultipleQueries = async(input: algoliaQuery[]) => {
    return client.multipleQueries(input);
  };

  return {
    algoliaClubSearch,
    algoliaProductSearch,
    algoliaMultipleQueries,
    algoliaSellerSearch,
  };
}
